import {
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addState,
  flowMax,
} from 'ad-hok'
import cx from 'classnames'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { Roundup as ApiRoundup, UsersWithImages } from 'shared/src/api/types'
import { addNavigate } from 'shared/src/utils/addNavigate'
import { addAuthContext } from 'shared/src/utils/auth'
import {
  DataLoadingState,
  makeDataErrorCold,
  makeDataLoaded,
  makeDataLoadingCold,
} from 'shared/src/utils/dataLoading'
import { typedAs } from 'shared/src/utils/typedAs'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { sansSerifFontFamily } from 'utils/styles'
import { setPageTitle } from 'utils/title'
import savedProductsPlaceholder from '../../assets/saved-products-placeholder.svg'
import { addLoadingAndErrorIndicators } from '../../utils/addLoadingAndErrorIndicators'
import { ConstrainWidth, LAYOUT_MAX_WIDTH } from '../../utils/layout'
import { makeRoundupUrl } from '../../utils/routes'
import {
  ROUNDUP_HEADER_Z_INDEX,
  ROUNDUP_RIGHT_BAR_Z_INDEX,
} from '../../utils/zIndex'
import { addMenuPage } from '../menu'
import { NavBar } from '../navbar'
import { Media } from './Media'

const useStyles = createUseStyles({
  container: {
    background: '#000000',
    minHeight: '100%',
    position: 'relative',
  },
  backButtonContainer: {
    position: 'fixed',
    paddingTop: 24,
    paddingLeft: 24,
    height: 44,
    display: 'flex',
    alignItems: 'center',
  },
  header: {
    height: 44,
    display: 'flex',
    alignItems: 'center',
    paddingTop: 24,
    paddingLeft: 80,
    zIndex: ROUNDUP_HEADER_Z_INDEX,
  },
  innerContainer: {
    paddingTop: 106,
  },
  inspirationButton: {
    marginBottom: 32,
  },
  followsContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: LAYOUT_MAX_WIDTH,
    flexGrow: 1,
  },
  followsHeader: {
    textDecoration: 'none',
    fontFamily: sansSerifFontFamily,
    fontWeight: 700,
    fontSize: 14,
    lineHeight: '17px',
    letterSpacing: '0.02em',
    color: '#111111',
    marginBottom: 20,
    marginLeft: 28,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  followsPlaceholder: {
    marginLeft: 28,
    marginBottom: 48,
    width: 'calc(100% - 28px * 2)',
  },
  mediaContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 40,
    paddingLeft: 40,
    paddingRight: 40,
    overflow: 'hidden',
  },
  media: {
    marginTop: 90,
  },
  media1Product: {
    marginBottom: 10,
  },
  media2Products: {
    marginBottom: 70,
  },
  media3Products: {
    marginBottom: 130,
  },
  mediaAndDecorationContainer: {
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'space-between',
  },
  mediaOdd: {},
  mediaEven: {
    flexDirection: 'row-reverse',
  },
  mediaPlaceholderImage: {
    width: '100%',
  },
  decoration: {
    marginTop: 160,
    marginLeft: -90,
    marginRight: -90,
  },
  rightBar: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    position: 'absolute',
    right: 0,
    top: 55,
    bottom: 36,
    pointerEvents: 'none',
  },
  rightBarInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pointerEvents: 'auto',
    zIndex: ROUNDUP_RIGHT_BAR_Z_INDEX,
    transform: 'rotate(90deg) translate(0, -24px)',
  },
})

export const Roundup: FC = flowMax(
  addDisplayName('Roundup'),
  addTranslationHelpers,
  setPageTitle(({ t }) => t('roundup.title')),
  addState(
    'roundup',
    'setRoundup',
    typedAs<DataLoadingState<ApiRoundup>>(makeDataLoadingCold())
  ),
  addState(
    'creators',
    'setCreators',
    typedAs<DataLoadingState<UsersWithImages>>(makeDataLoadingCold())
  ),
  addAuthContext,
  addApi,
  addMenuPage,
  addEffect(
    ({ setRoundup, setCreators, navigateToLogin, isAuthenticated, api }) =>
      () => {
        if (!isAuthenticated) {
          navigateToLogin({ loginTrigger: 'roundup' })
        }

        api
          .getRoundup()
          .then(makeDataLoaded)
          .catch(makeDataErrorCold)
          .then(setRoundup)

        api.follows
          .creators()
          .then(makeDataLoaded)
          .catch(makeDataErrorCold)
          .then(setCreators)
      },
    ['isAuthenticated']
  ),
  addLoadingAndErrorIndicators('roundup'),
  addLoadingAndErrorIndicators('creators'),
  addProps(() => ({
    classes: useStyles(),
  })),
  addNavigate,
  addHandlers({
    navigateToRoundup:
      ({ navigate }) =>
      () => {
        navigate(makeRoundupUrl())
      },
  }),
  ({ roundup: { groups }, classes }) => (
    <ConstrainWidth fillHeight>
      <div className={classes.container}>
        <NavBar titleTranslationKey="roundup.navbarTitle" />
        <div className={classes.innerContainer}>
          <div className={classes.mediaContainer}>
            {groups.length > 0 ? (
              groups.map((group, index) => (
                <div
                  className={cx(
                    classes.mediaAndDecorationContainer,
                    index % 2 === 0 ? classes.mediaOdd : classes.mediaEven
                  )}
                  key={index}
                >
                  <Media
                    group={group}
                    className={cx(
                      classes.media,
                      group.products.length === 3
                        ? classes.media3Products
                        : group.products.length === 2
                        ? classes.media2Products
                        : classes.media1Product
                    )}
                  />
                </div>
              ))
            ) : (
              <div
                className={cx(
                  classes.mediaAndDecorationContainer,
                  classes.mediaOdd
                )}
              >
                <img
                  src={savedProductsPlaceholder}
                  alt=""
                  className={classes.mediaPlaceholderImage}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </ConstrainWidth>
  )
)
